import { format } from 'date-fns/format';
import isBoolean from 'lodash/isBoolean';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import { ZodError } from 'zod';

export function getErrorMessage(ex: unknown) {
  if (ex instanceof ZodError) {
    const errorDetails = Object.values(
      ex.flatten((issue) => ({
        message: `Invalid request: '${issue.path.join('.')}' is ${
          issue.message
        }`,
        errorCode: issue.code,
      })).fieldErrors,
    );

    const formattedMessage = errorDetails
      .flatMap((item) => item)
      .map((item) => (item as { message: string })?.message)
      .filter(Boolean)
      .join(', ');

    return formattedMessage;
  } else if (ex instanceof Error) {
    return ex.message;
  }
  return JSON.stringify(ex);
}

export function getErrorStack(ex: unknown) {
  return ex instanceof Error ? ex.stack : null;
}

/**
 * @deprecated - should import from {@link @monetize/types/common} instead
 */
export const getRequiredMessage = (title: string) => `${title} is required.`; // error msg for empty input

export function ensureBoolean(value: unknown) {
  if (isBoolean(value)) {
    return value;
  } else if (isString(value)) {
    return value.toLowerCase().startsWith('t');
  }
  return false;
}

export function ensureNumber(value: unknown, defaultValue = 0) {
  if (isNumber(value) && !isNaN(value)) {
    return value;
  } else if (isString(value) && /^-?\d*(\.\d+)?$/.test(value)) {
    return parseFloat(value);
  }
  return defaultValue;
}

export function ensureArray(value: unknown) {
  if (Array.isArray(value)) {
    return value;
  } else if (isNil(value)) {
    return [];
  }
  return [value];
}

/**
 * Not 10000% perfect, but will ensure numbers that are close enough are considered equal
 * normalizing away precision
 *
 * @param a
 * @param b
 * @returns
 */
export function areNumbersEqual(a: number, b: number) {
  return Math.abs(a - b) < Number.EPSILON;
}

/**
 * Determines if a word should be shown in plural or singular form.
 *
 * Used when showing text such as like `1,024 accounts` or `1 account`.
 *
 * Examples:
 * * `pluralize('result', 1) => 'result'`
 * * `pluralize('result', 0) => 'results'`
 * * `pluralize('result', 42) => 'results'`
 * * `pluralize('result', 42, 'es', 'response') => 'response'`
 *
 * @param word String that will be turned into plural based on number of items
 * @param length number of items
 * @param plural defaults to `s` but can be changed based on the base word
 * @param pluralOverride [Conditional] overrides the plural form completely if provided
 * @returns singular or plural version of the word
 */
export function pluralize(
  word: string,
  length: number,
  plural = 's',
  pluralOverride?: string,
): string {
  if (isString(pluralOverride) && pluralOverride.length > 0) {
    return pluralOverride;
  }

  if (length !== 1) {
    return `${word}${plural}`;
  }

  return word;
}

/**
 *
 * @returns Array of objects with month names and values
 * Example: [{ title: 'January', value: 1 }, { title: 'February', value: 2 }, ...]
 */
export const getMonthOptions = () =>
  [...Array(12).keys()].map((k) => ({
    title: format(new Date(2000, k, 1), 'MMMM'),
    value: k + 1,
  }));

/**
 *
 * @param month inputs month (1-12)
 * @param year inputs year (defaults to 2000)
 * @returns returns an array of objects with day titles and values for the given month and year
 * Example: If month is 2 (February) and year is 2020, it will return
 * [{ title: '1', value: 1 }, { title: '2', value: 2 }, ..., { title: '29', value: 29 }]
 */
export const getDayOptions = (
  month: number | null | undefined,
  year = 2025,
) => {
  if (month == null || month < 1 || month > 12) {
    return [];
  }

  const daysInMonth = new Date(year, month, 0).getDate();
  return Array.from({ length: daysInMonth }, (_, i) => ({
    title: (i + 1).toString(),
    value: i + 1,
  }));
};
